import { postContentClass } from "@repo/fika-blog/src/classes"
import {
  postClass,
  postContainerClass,
  postDateClass,
  postImageClass,
} from "@repo/fika-blog/src/content.css"
import { extensions } from "@repo/fika-blog/src/extensions"
import {
  postDescriptionClass,
  postHeaderClass,
  postHeaderContentClass,
  postHeaderIconClass,
  postImageWrapperClass,
  postTitleClass,
} from "@repo/fika-blog/src/postHeader.css"
import { renderToHTMLString } from "@repo/fika-blog/src/renderer/pm/html-string/html-string"
import { cdnClass } from "@repo/fika-design-system/cdnClass"
import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import { useNavigate, useParams } from "@solidjs/router"
import { format, intlFormatDistance } from "date-fns"
import { sortBy } from "remeda"
import { createMemo, createSelector, For, Show } from "solid-js"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { Card } from "@/designSystem/patterns/card"
import { Navigation } from "@/designSystem/patterns/navigation"
import { type Pill, Pills } from "@/designSystem/patterns/pills"
import { postStatusPill } from "@/designSystem/patterns/post"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/getTitle"
import { createBlogRouter } from "@/routes/posts-router"
import { postVersionClass, postVersionTitleClass } from "./post-versions.css"
import usePost from "./use-post"

export function PostVersions() {
  const post = usePost()

  return <Show when={post()}>{post => <SafePostVersions post={post()} />}</Show>
}

function SafePostVersions(props: { post: Post }) {
  const { LL } = useI18nContext()
  const { db, replicache } = useAppContext()
  const navigate = useNavigate()
  const title = createMemo(() => getTitle(props.post.title))

  const { paths } = createBlogRouter()
  const params = useParams()

  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: title(),
        href: paths().post(props.post).url,
      } as const,
    ]
  })
  const postVersions = createMemo(() =>
    sortBy(db.post.filterBy({ root_id: props.post.id }), [
      post => post.created_at,
      "desc",
    ]),
  )
  const postVersion = createMemo(() => {
    if (!params.versionId) return props.post

    return postVersions()?.find(post => post.id === params.versionId)!
  })
  const postVersionId = createMemo(() => postVersion()?.id)
  const isSelected = createSelector(postVersionId)

  async function restoreVersion() {
    const versionId = ulid()
    if (postVersion()?.latest) return

    await replicache.mutate.versionPost({
      id: props.post.id,
      version_id: versionId,
      title: postVersion()?.title,
      description: postVersion()?.description,
      json: postVersion()?.json,
    })

    navigate(paths().post(props.post).versions(props.post.id))
  }

  return (
    <MainLayout
      header={<Navigation hue="amber" buttons={buttons()} title="versions" />}
      leftPanel={
        <Show when={postVersions()}>
          {postVersions => (
            <Box gap="12" background="sand100" padding="12">
              <For each={postVersions()}>
                {postVersion => (
                  <PostVersion
                    postVersion={postVersion}
                    post={props.post}
                    isSelected={isSelected(postVersion.id)}
                  />
                )}
              </For>
            </Box>
          )}
        </Show>
      }
      footer={
        <ButtonDrawer
          hue="amber"
          rightActions={
            <Button
              hue="amber"
              iconName="book-dashed"
              onClick={restoreVersion}
              disabled={postVersion()?.latest}
            >
              restore
            </Button>
          }
        />
      }
    >
      <Show when={postVersion()}>
        {postVersion => (
          <PostPreview
            post={postVersion()}
            footerHTML={null}
            headerHTML={null}
          />
        )}
      </Show>
    </MainLayout>
  )
}

function PostVersion(props: {
  postVersion: Post
  post: Post
  isSelected: boolean
}) {
  const { paths } = createBlogRouter()
  const pills = createMemo(() => {
    const pills: Pill[] = []

    if (props.postVersion.latest) {
      pills.push({
        icon: "star",
        label: "latest",
        hue: "amber",
      })
    }

    pills.push(postStatusPill(props.postVersion))

    return pills
  })
  const formatDate = createMemo(() =>
    format(props.postVersion.created_at, "PPp"),
  )

  return (
    <Card
      href={paths().post(props.post).versions(props.postVersion.id)}
      logo={<Icon name="history" color="amber600" />}
      hue="amber"
      selected={props.isSelected}
    >
      <div class={postVersionClass}>
        <Box>
          <div class={postVersionTitleClass}>
            {getTitle(props.postVersion.title)}
          </div>
          <Text fontSize="s" color="sand800" lineClamp="1">
            <time title={formatDate()} datetime={formatDate()}>
              {intlFormatDistance(props.postVersion.created_at, new Date())}
            </time>
          </Text>
        </Box>

        <Pills pills={pills()} />
      </div>
    </Card>
  )
}

type PostPreviewProps = {
  post: Post
  footerHTML: string | null
  headerHTML: string | null
}
function PostPreview(props: PostPreviewProps) {
  const html = createMemo(() => {
    const json = props.post.json
    if (!json) return null

    const content = JSON.parse(json)

    return renderToHTMLString({
      extensions: extensions({ dropcursor: false }),
      content,
    })
  })

  return (
    <div class={postContainerClass}>
      <div class={postClass}>
        <div class={postHeaderClass}>
          <Show when={props.post.image_header_url}>
            {imageUrl => (
              <div class={postImageWrapperClass}>
                <img
                  src={imageUrl()}
                  alt={props.post.title ?? ""}
                  class={postImageClass}
                  srcset={`
                                        ${cdnClass(imageUrl(), "post300")} 300w,
                                        ${cdnClass(imageUrl(), "post400")} 400w,
                                        ${cdnClass(imageUrl(), "post600")} 600w,
                                        ${cdnClass(imageUrl(), "post1000")} 1000w
                                    `}
                  sizes="
                                        (max-width: 400px) 300px,
                                        (max-width: 500px) 400px,
                                        (max-width: 600px) 600px,
                                        1000px
                                    "
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
          </Show>
          <div class={postHeaderContentClass}>
            <div class={postHeaderIconClass}>
              <Icon color="amber600" name="pen-tool" size="24" />
            </div>
            <h1 class={postTitleClass}>{getTitle(props.post.title)}</h1>
            <h2 class={postDescriptionClass}>{props.post.description}</h2>
            <Show
              when={props.post.published_at}
              fallback={
                <p class={postDateClass}>this is an unpublished draft</p>
              }
            >
              {publishedAt => (
                <p
                  class={postDateClass}
                >{`${intlFormatDistance(publishedAt(), new Date())}`}</p>
              )}
            </Show>
          </div>
        </div>
        <div class={postContentClass} innerHTML={html() ?? ""} />
      </div>
    </div>
  )
}
